import { FC, useCallback, useEffect, useState } from 'react';
import integrationsService from 'services/api/integrations.service';
import ButtonBase from 'components/ui/buttons/ButtonBase';

type ShipmentEpcisStatus = {
    toteId: number;
    toteStatus: string;
    outboundEpcisId: number | null;
    missingOutboundEpcis: boolean;
    canRegenerate: boolean;
    reason?: string;
};

type Props = {
    toteId: number;
    toteStatus: string;
};

const ToteShipmentEpcisRecovery: FC<Props> = ({ toteId, toteStatus }) => {
    const [status, setStatus] = useState<ShipmentEpcisStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const isShipped = toteStatus === 'SHIPPED' || toteStatus === 'DELIVERED';

    const loadStatus = useCallback(async () => {
        if (!isShipped) {
            setStatus(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res: any = await integrationsService.getToteShipmentEpcisStatus(toteId);
            setStatus(res.data ?? null);
        } catch {
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, [isShipped, toteId]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        setSuccess(null);
        try {
            const res: any = await integrationsService.generateToteShipmentEpcis(toteId);
            const data = res.data;
            if (data?.alreadyExisted) {
                setSuccess(`Outbound EPCIS already exists (id ${data.epcisId})`);
            } else {
                setSuccess(`Outbound EPCIS created (id ${data?.epcisId}) — AS2 dispatch triggered`);
            }
            await loadStatus();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to generate outbound EPCIS');
        } finally {
            setGenerating(false);
        }
    };

    if (!isShipped) {
        return null;
    }

    if (loading && !status) {
        return <p className="mt-2 text-xs text-slate-500">Checking outbound EPCIS status…</p>;
    }

    if (!status?.canRegenerate) {
        return null;
    }

    return (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">Outbound EPCIS missing</p>
            <p className="mt-1 text-xs text-amber-800">
                {status.reason || 'Ship completed but outbound EPCIS file was not created.'}
            </p>
            {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
            {success && <p className="mt-2 text-xs text-green-700">{success}</p>}
            <ButtonBase
                innerText={generating ? 'Generating…' : 'Generate outbound EPCIS'}
                onClick={handleGenerate}
                disabled={generating}
                className="mt-2 bg-amber-600 hover:bg-amber-700 rounded border-none px-3 py-1.5 text-sm text-white disabled:opacity-60"
            />
        </div>
    );
};

export default ToteShipmentEpcisRecovery;
